import { Component, input, model, OnInit, output } from '@angular/core';

import { translate } from '@jsverse/transloco';

import { Group, GroupService } from 'app/providers/semantria-api/group.service';
import { IndustryPackService } from 'app/providers/semantria-api/industry-pack.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
  SemantriaConfigurationDto,
} from '../../../../providers/semantria-api/configuration.service';
import { SemantriaAccountService } from '../../../../providers/semantria-api/semantria-account.service';
import { AlertService } from '../../../../services/alert.service';
import { BriefIndustryPack } from './../../../../providers/semantria-api/semantria-account.service';
import { ConfigurationIncludeData } from '../configuration-content-form/configuration-content-form.component';
import { ImportStrategy } from './import-strategy';
import { MergeImportStrategy } from './merge-import-strategy';
import { NoopImportStrategy } from './noop-import-strategy';
import { OverrideImportStrategy } from './override-import-strategy';

export type ConfigType = 'standard' | 'layered';

export interface ImportConfigurationDialogOptions {
  groupId: string;
  inputFile?: any;
  sourceConfigDto: SemantriaConfigurationDto;
  targetConfigDto?: SemantriaConfigurationDto;
  configType: ConfigType;
}

export interface ConfirmImportConfigurationDialogOptions {
  source: string;
  action: 'merge' | 'override';
  items: Map<string, number>;
  description: boolean;
  tags: boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-import-into-configuration-drawer',
  templateUrl: './import-into-configuration-drawer.component.html',
  styleUrls: ['./import-into-configuration-drawer.component.scss'],
  standalone: false,
})
export class ImportIntoConfigurationDrawerComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly groupId = input<string>();
  readonly open = input<boolean>();
  readonly inputFile = input<any>();
  readonly sourceConfiguration = model<SemantriaConfigurationDto>();
  readonly targetConfiguration = input<SemantriaConfigurationDto>();
  readonly accountLimits = input<any>();

  readonly close = output<SemantriaConfiguration | undefined>();

  readonly configItems = [
    {
      label: translate('import-into-configuration-drawer-component.sentiment-phrases'),
      prop: 'sentiment_phrases',
      count: 0,
      include: true,
      settings: 'SENTIMENT_PHRASES',
    },
    {
      label: translate('import-into-configuration-drawer-component.query-topics'),
      prop: 'query_topics',
      count: 0,
      include: true,
      settings: 'QUERY_TOPICS',
    },
    {
      label: translate('import-into-configuration-drawer-component.user-entities'),
      prop: 'user_entities',
      count: 0,
      include: true,
      settings: 'USER_ENTITIES',
    },
    {
      label: translate('import-into-configuration-drawer-component.taxonomy'),
      prop: 'taxonomies',
      count: 0,
      include: true,
      settings: 'TAXONOMY',
    },
    {
      label: translate('import-into-configuration-drawer-component.concept-topics'),
      prop: 'concept_topics',
      count: 0,
      include: true,
      settings: 'CONCEPT_TOPICS',
    },
    {
      label: translate('import-into-configuration-drawer-component.blacklist-items'),
      prop: 'blacklist_items',
      count: 0,
      include: true,
      settings: 'BLACKLISTS',
    },
  ];
  itemCounts = new Map<string, number>();

  accountFeatures: string[] = [];
  missingAccountFeatures: string[] = [];

  configIncludeData: ConfigurationIncludeData;

  action: 'merge' | 'override' = 'merge';
  importStrategy: ImportStrategy = new NoopImportStrategy();
  resultingConfiguration: SemantriaConfigurationDto;
  showConfirmImport: boolean;
  confirmDialogData: ConfirmImportConfigurationDialogOptions;

  fileName: string;
  fileType: string;

  industryPacks: BriefIndustryPack[];
  configsByGroup = new Map<Group, SemantriaConfiguration[]>();
  selectedSourceConfigId: string;

  isLoading = true;
  isSaving: boolean;
  needReload: boolean;

  constructor(
    private accountService: SemantriaAccountService,
    private configService: ConfigurationService,
    private industryPackService: IndustryPackService,
    private groupService: GroupService,
    private alertService: AlertService
  ) {}

  async ngOnInit() {
    this.resultingConfiguration = JSON.parse(JSON.stringify(this.targetConfiguration()));

    const accountDetails = await this.accountService.sub.latest();
    this.accountFeatures = accountDetails.features;

    if (this.inputFile()) {
      const parts = this.inputFile().name.split('.');
      this.fileName = parts[0];
      this.fileType = parts[parts.length - 1];

      this.onActionChanged();
    } else {
      this.industryPacks = (accountDetails.industry_packs || [])
        .filter((p) => p.language_id === this.targetConfiguration().language_id)
        .sort((l1, l2) => l1.name.localeCompare(l2.name));

      const visibleGroups = this.groupService.getVisibleGroups();
      const configurations = await this.configService.getConfigurations();

      for (const group of visibleGroups) {
        const groupConfigs = configurations.filter(
          (c) =>
            c.group === group.group_id &&
            c.language_id === this.targetConfiguration().language_id &&
            c.id !== this.targetConfiguration().id &&
            !c.layer
        );

        if (groupConfigs.length) {
          this.configsByGroup.set(group, groupConfigs);
        }
      }
    }

    this.isLoading = false;
  }

  selectSourceConfig(source, type: 'pack' | 'configuration') {
    this.selectedSourceConfigId = source.id;
    this.isSaving = true;

    const exportPromise =
      type === 'pack'
        ? this.industryPackService.exportIndustryPack(source.id)
        : this.configService.exportConfiguration(source.id);

    exportPromise
      .then((res) => {
        this.sourceConfiguration.set(res);
        this.onActionChanged();
      })
      .finally(() => (this.isSaving = false));
  }

  updateItemCounts() {
    const counts = this.importStrategy.countItems(
      this.sourceConfiguration(),
      this.targetConfiguration(),
      this.configItems
    );

    for (const prop of counts.keys()) {
      if (counts.get(prop) !== this.itemCounts.get(prop)) {
        this.itemCounts = counts;
        break;
      }
    }

    this.configItems.forEach((item) => (item.count = this.itemCounts.get(item.prop)));
  }

  updateDescription() {
    let notes = this.targetConfiguration().notes;

    if (this.configIncludeData?.includes.notes) {
      notes = this.importStrategy.assembleNotes(
        this.sourceConfiguration(),
        this.targetConfiguration()
      );
    }

    this.resultingConfiguration.notes = notes;
  }

  updateTags() {
    let tags = this.targetConfiguration().tags || [];

    if (this.configIncludeData?.includes.tags) {
      tags = this.importStrategy.assembleTags(
        this.sourceConfiguration(),
        this.targetConfiguration()
      );
    }

    this.resultingConfiguration.tags = tags;
  }

  onActionChanged() {
    switch (this.action) {
      case 'merge':
        this.importStrategy = new MergeImportStrategy();
        break;
      case 'override':
        this.importStrategy = new OverrideImportStrategy();
        break;
    }

    this.updateItemCounts();
    this.updateDescription();
    this.updateTags();

    const importResult = this.importStrategy.doImport(
      this.sourceConfiguration(),
      this.targetConfiguration(),
      {
        valid: true,
        includes: {
          notes: this.configIncludeData.includes.notes,
          tags: this.configIncludeData.includes.tags,
          sentiment_phrases: true,
          query_topics: true,
          user_entities: true,
          taxonomies: true,
          concept_topics: true,
          blacklist_items: true,
        },
      }
    );
    this.resultingConfiguration = importResult.configDto;
  }

  onConfigIncludeChanged($event) {
    this.configIncludeData = $event;

    Object.keys(this.configIncludeData.includes)
      .filter((k) => k !== 'notes' && k !== 'tags')
      .forEach(
        (k) =>
          (this.configItems.find((i) => i.prop === k).include = this.configIncludeData.includes[k])
      );

    this.updateItemCounts();
    this.updateDescription();
    this.updateTags();
  }

  onAccountFeatureChecked($event) {
    this.missingAccountFeatures = $event;
  }

  canConfirmImport() {
    return (
      this.sourceConfiguration &&
      this.configIncludeData &&
      this.configIncludeData.valid &&
      !this.isSaving
    );
  }

  confirmImport() {
    this.confirmDialogData = {
      source: this.resultingConfiguration.name,
      action: this.action,
      items: this.configItems
        .filter((item) => this.configIncludeData.includes[item.prop])
        .reduce((prev, curr) => prev.set(curr.label, curr.count), new Map<string, number>()),
      description: this.configIncludeData.includes.notes,
      tags: this.configIncludeData.includes.tags,
    };
    this.showConfirmImport = true;
  }

  async onConfirmImportClosed(success) {
    this.showConfirmImport = false;

    if (!success) {
      return;
    }

    this.isSaving = true;

    this.alertService.showToast(
      translate('import-into-configuration-drawer-component.importing-data-into-configuration')
    );

    try {
      const importResult = this.importStrategy.doImport(
        this.sourceConfiguration(),
        this.targetConfiguration(),
        this.configIncludeData
      );

      const syncConfigItems = async (prop, deleteApi, createApi, updateApi) => {
        let newItems = [];

        if (importResult.itemsToDelete[prop]?.length) {
          await deleteApi(
            importResult.configDto.id,
            importResult.itemsToDelete[prop].map((item) => item.id)
          );
        }

        if (importResult.itemsToCreate[prop]?.length) {
          newItems = await createApi(
            importResult.configDto.id,
            importResult.itemsToCreate[prop].map((item) => ({ ...item, id: undefined }))
          );
        }

        if (importResult.itemsToUpdate[prop]?.length) {
          await updateApi(importResult.configDto.id, importResult.itemsToUpdate[prop]);
        }

        return newItems;
      };

      syncConfigItems(
        'sentiment_phrases',
        this.configService.deleteBatchSentimentPhrases.bind(this.configService),
        this.configService.createBatchSentimentPhrases.bind(this.configService),
        this.configService.saveBatchSentimentPhrases.bind(this.configService)
      );

      syncConfigItems(
        'blacklist_items',
        this.configService.deleteBatchBlacklistEntries.bind(this.configService),
        this.configService.createBatchBlacklistEntries.bind(this.configService),
        this.configService.saveBatchBlacklistEntries.bind(this.configService)
      );

      // Have to delete taxonomy tree first to make sure related targets can be deleted later
      if (importResult.itemsToDelete.taxonomies?.length) {
        await this.configService.deleteBatchTaxonomyNodes(
          importResult.configDto.id,
          importResult.itemsToDelete.taxonomies.map((item) => item.id)
        );
      }

      const queryTopicsCreated = await syncConfigItems(
        'query_topics',
        this.configService.deleteBatchQueryTopics.bind(this.configService),
        this.configService.createBatchQueryTopics.bind(this.configService),
        this.configService.saveBatchQueryTopics.bind(this.configService)
      );

      const entitiesCreated = await syncConfigItems(
        'user_entities',
        this.configService.deleteBatchUserEntities.bind(this.configService),
        this.configService.createBatchUserEntities.bind(this.configService),
        this.configService.saveBatchUserEntities.bind(this.configService)
      );

      const conceptTopicsCreated = await syncConfigItems(
        'concept_topics',
        this.configService.deleteBatchConceptTopics.bind(this.configService),
        this.configService.createBatchConceptTopics.bind(this.configService),
        this.configService.saveBatchConceptTopics.bind(this.configService)
      );

      const mapOldIdsToNew = (prop, newItems, equalsFn) => {
        const idMap = new Map<string, string>();

        importResult.itemsToCreate[prop]?.forEach((oldItem) => {
          idMap.set(oldItem.id, newItems.find((newItem) => equalsFn(oldItem, newItem)).id);
        });

        return idMap;
      };

      const targetItemIdMap = {
        QUERY_TOPIC: mapOldIdsToNew(
          'query_topics',
          queryTopicsCreated,
          (oldItem, newItem) => oldItem.name === newItem.name
        ),
        USER_ENTITY: mapOldIdsToNew(
          'user_entities',
          entitiesCreated,
          (oldItem, newItem) =>
            (oldItem.normalized &&
              newItem.normalized &&
              oldItem.normalized === newItem.normalized) ||
            oldItem.name === newItem.name
        ),
        CONCEPT_TOPIC: mapOldIdsToNew(
          'concept_topics',
          conceptTopicsCreated,
          (oldItem, newItem) => oldItem.name === newItem.name
        ),
      };

      const replaceTargetItemIds = (taxonomyNodes) => {
        if (taxonomyNodes) {
          taxonomyNodes.forEach((node) => {
            if (node.elements) {
              node.elements.forEach((target) => {
                target.target_id =
                  targetItemIdMap[target.target_type]?.get(target.target_id) || target.target_id;
              });
            }

            replaceTargetItemIds(node.children);
          });
        }
      };

      replaceTargetItemIds(importResult.itemsToCreate.taxonomies);
      replaceTargetItemIds(importResult.itemsToUpdate.taxonomies);

      if (importResult.itemsToCreate.taxonomies?.length) {
        await this.configService.createBatchTaxonomyNodes(
          importResult.configDto.id,
          importResult.itemsToCreate.taxonomies.map((item) => ({ ...item, id: undefined }))
        );
      }

      if (importResult.itemsToUpdate.taxonomies?.length) {
        await this.configService.saveBatchTaxonomyNodes(
          importResult.configDto.id,
          importResult.itemsToUpdate.taxonomies
        );
      }

      const configuration = await this.configService.updateConfiguration(importResult.configDto);

      this.alertService.showToast(
        translate('import-into-configuration-drawer-component.import-finished')
      );

      await this.groupService.refreshAndSelectActiveGroup();
      this.close.emit(configuration);
    } catch (e) {
      this.isSaving = false;
      this.needReload = true;

      let msg = e.error.error || Object.values(e.error['violations'])[0];
      if (msg.includes('error_message')) {
        msg = translate('import-into-configuration-drawer-component.error-message-base');
      }
      this.alertService.showErrorToast(
        translate('import-into-configuration-drawer-component.failed-to-import', { msg })
      );
    }
  }

  cancel(restart?) {
    if (this.needReload) {
      window.location.reload();
      return;
    }

    this.close.emit(restart);
  }
}
